import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { formatApiError } from '../utils/errorFormatter';
import { PublicPageCopy } from '../components/PublicPageCopy';
import { 
  Mail, 
  Lock, 
  User, 
  ArrowRight, 
  Loader2, 
  ShieldCheck, 
  Building, 
  Briefcase,
  CheckCircle,
  Users,
  Rocket,
  Handshake,
  Phone
} from "lucide-react";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [organization, setOrganization] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [role, setRole] = useState("user"); // user, staff, physician, admin
  const [discipline, setDiscipline] = useState(""); // For staff/physician: rn, lpn, msw, chaplain
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [invitedCompany, setInvitedCompany] = useState(null); // {name} from an invite link
  const [codeLocked, setCodeLocked] = useState(false);
  const [inviteToken, setInviteToken] = useState("");
  const [inviteError, setInviteError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Invite links:
  //   /register?invite=<token>  — secure link (may expire / be single-use)
  //   /register?code=EVERHEART&role=staff — plain pre-fill link (no expiry)
  // Both pre-fill so staff never mistype the code.
  useEffect(() => {
    const token = (searchParams.get("invite") || "").trim();
    if (token) {
      setCodeLocked(true); // lock immediately; unlocked only if the token is invalid
      axios.get(`${API}/company/public/invite/${encodeURIComponent(token)}`)
        .then((r) => {
          const d = r.data || {};
          if (d.status === "valid") {
            setInviteToken(token);
            setCompanyCode((d.company_code || "").toUpperCase());
            setCodeLocked(true);
            if (["user", "staff", "physician"].includes(d.role)) setRole(d.role);
            setInvitedCompany({ company_name: d.company_name, company_code: d.company_code });
            setInviteError("");
          } else {
            setCodeLocked(false);
            const msg = {
              expired: "This invite link has expired. Ask your administrator for a new one.",
              used: "This invite link has already been used. Ask your administrator for a new one.",
              revoked: "This invite link has been revoked. Ask your administrator for a new one.",
            }[d.status] || "This invite link is no longer valid. Ask your administrator for a new one.";
            setInviteError(msg);
          }
        })
        .catch(() => { setCodeLocked(false); setInviteError("This invite link is invalid. Ask your administrator for a new one."); });
      return;
    }
    const code = (searchParams.get("code") || "").trim().toUpperCase();
    const invitedRole = (searchParams.get("role") || "").trim().toLowerCase();
    if (code) {
      setCompanyCode(code);
      setCodeLocked(true);
      if (["user", "staff", "physician"].includes(invitedRole)) setRole(invitedRole);
      axios.get(`${API}/company/public/by-code/${encodeURIComponent(code)}`)
        .then((r) => setInvitedCompany(r.data))
        .catch(() => setInvitedCompany(null));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if ((role === "staff" || role === "physician") && !discipline) {
      toast.error("Please select your discipline");
      return;
    }

    if (role === "admin" && !companyName) {
      toast.error("Please enter your company/organization name");
      return;
    }

    if (role !== "admin" && !companyCode.trim()) {
      toast.error("Please enter your company code. Ask your hospice administrator for it.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        organization: role === "admin" ? companyName : organization,
        job_title: jobTitle,
        role: role === "admin" ? "user" : role, // Backend handles admin role separately
        discipline: (role === "staff" || role === "physician") ? discipline : null,
        register_as_admin: role === "admin",
        company_name: role === "admin" ? companyName : null,
        company_domain: role === "admin" ? companyDomain : null,
        company_code: companyCode.trim() || null,
        invite_token: inviteToken || null,
      });
      
      if (response.data.token) {
        // P3 Cookie Migration: backend already set the HttpOnly cookie on
        // /register — nothing to persist in JS. Just navigate.
        toast.success(
          role === "admin" 
            ? "Organization created successfully! Welcome to The HospiceGuard." 
            : "Account created successfully! Welcome to The HospiceGuard."
        );
        navigate("/app/dashboard");
        window.location.reload();
      }
    } catch (error) {
      toast.error(formatApiError(error, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700">
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">The HospiceGuard</h1>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-4xl font-bold text-white leading-tight">
              Create Your<br />Account
            </h2>
            <p className="text-emerald-100 text-lg max-w-md">
              Join our HIPAA-compliant hospice audit platform. Get instant access to AI-powered documentation tools.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-emerald-100">
                <CheckCircle className="w-5 h-5" />
                <span>Instant account activation</span>
              </div>
              <div className="flex items-center gap-3 text-emerald-100">
                <CheckCircle className="w-5 h-5" />
                <span>AI-powered compliance analysis</span>
              </div>
              <div className="flex items-center gap-3 text-emerald-100">
                <CheckCircle className="w-5 h-5" />
                <span>HIPAA-compliant documentation</span>
              </div>
            </div>

            <div className="pt-4 border-t border-white/20 space-y-2">
              <div className="flex items-center gap-2 text-white font-semibold text-sm">
                <Handshake className="w-4 h-4" />
                <span>Partnership inquiries</span>
              </div>
              <p className="text-emerald-100 text-xs max-w-md">
                Consultant, reseller, or enterprise hospice network? Let&apos;s talk.
              </p>
              <div className="space-y-1">
                <a
                  href="mailto:nsarpong@goodwinliving.org?subject=HospiceGuard%20Partnership%20Inquiry"
                  className="flex items-center gap-2 text-emerald-100 hover:text-white text-sm transition"
                >
                  <Mail className="w-4 h-4" />
                  nsarpong@goodwinliving.org
                </a>
                <a
                  href="tel:+15713296603"
                  className="flex items-center gap-2 text-emerald-100 hover:text-white text-sm transition"
                >
                  <Phone className="w-4 h-4" />
                  (571) 329-6603
                </a>
              </div>
            </div>
          </div>
          
          <p className="text-emerald-200 text-sm">
            The HospiceGuard Hospice Audit System - HIPAA Compliant
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold">The HospiceGuard</span>
            </div>
            <p className="text-sm text-muted-foreground">Hospice Audit System</p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="pt-6">
              <div className="space-y-2 mb-4">
                <h2 className="text-2xl font-bold">Sign Up</h2>
                <p className="text-muted-foreground">
                  Pick the path that fits — you can get started in under a minute.
                </p>
              </div>

              {/* Path chooser — 3 tap-to-select tiles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6" data-testid="signup-path-chooser">
                <button
                  type="button"
                  data-testid="path-join-existing"
                  onClick={() => {
                    setRole("user");
                    setCompanyName("");
                    setCompanyDomain("");
                  }}
                  className={`text-left p-3 rounded-lg border-2 transition ${
                    role !== "admin"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 bg-white hover:border-emerald-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-sm">Join an existing org</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    My hospice already uses HospiceGuard — I want to join.
                  </p>
                </button>

                <button
                  type="button"
                  data-testid="path-start-new-org"
                  onClick={() => {
                    setRole("admin");
                    setTimeout(() => {
                      document
                        .getElementById("companyName")
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                  }}
                  className={`text-left p-3 rounded-lg border-2 transition ${
                    role === "admin"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 bg-white hover:border-emerald-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Rocket className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-sm">Start a new organization</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Consultant, new agency, or trial — creates your own workspace as admin.
                  </p>
                </button>
              </div>

              {role === "admin" && (
                <div className="mb-4 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
                  <Building className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    You&apos;ll be the <strong>administrator</strong> of your new workspace. Add
                    teammates any time from Settings after you sign in.
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="name-input"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      placeholder="john@hospice.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {role !== "admin" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="organization"
                          placeholder="ABC Hospice"
                          value={organization}
                          onChange={(e) => setOrganization(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">Job Title</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="jobTitle"
                          placeholder="Nurse"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {role !== "admin" && (
                  <div className="space-y-2 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <Label htmlFor="companyCode" className="text-sm font-medium">
                      Company Code <span className="text-red-500">*</span>
                    </Label>
                    {invitedCompany?.company_name && (
                      <div className="flex items-center gap-2 text-sm text-cyan-800 bg-white border border-cyan-200 rounded-md px-3 py-2" data-testid="invite-banner">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        You're joining <span className="font-semibold">{invitedCompany.company_name}</span>
                      </div>
                    )}
                    {inviteError && (
                      <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="invite-error">
                        <ShieldCheck className="w-4 h-4 text-red-500" />
                        {inviteError}
                      </div>
                    )}
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="companyCode"
                        data-testid="company-code-input"
                        placeholder="e.g. EVERHEART"
                        value={companyCode}
                        onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                        className="pl-10 font-mono tracking-wider"
                        readOnly={codeLocked}
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {codeLocked
                        ? "Pre-filled from your invite link — no need to change it."
                        : "Ask your hospice administrator for your organization's code. This links your account to your team's shared workspace."}
                    </p>
                  </div>
                )}


                {/* Role Selection */}
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-sm font-medium">
                    I am registering as
                  </Label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      if (e.target.value !== "staff") setDiscipline("");
                      if (e.target.value !== "admin") {
                        setCompanyName("");
                        setCompanyDomain("");
                      }
                    }}
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="role-select"
                  >
                    <option value="user">Clinical User (Auditor/Manager)</option>
                    <option value="staff">Staff (RN/MSW/Chaplain/HHA)</option>
                    <option value="physician">Physician</option>
                    <option value="admin">Organization Admin (Create New Org)</option>
                  </select>
                </div>

                {/* Admin Registration - Company Details */}
                {role === "admin" && (
                  <div className="space-y-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2 text-emerald-700 font-medium">
                      <Building className="w-4 h-4" />
                      <span>Create Your Organization</span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-sm font-medium">
                        Organization Name <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="companyName"
                          data-testid="company-name-input"
                          placeholder="Sunrise Hospice Care"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyDomain" className="text-sm font-medium">
                        Company Domain (optional)
                      </Label>
                      <Input
                        id="companyDomain"
                        placeholder="sunrisehospice.com"
                        value={companyDomain}
                        onChange={(e) => setCompanyDomain(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Users with this email domain can auto-join your organization
                      </p>
                    </div>
                    <div className="text-xs text-emerald-600 bg-emerald-100 p-2 rounded">
                      ✓ 14-day free trial • ✓ Up to 10 users • ✓ 100 audits/month
                    </div>
                  </div>
                )}

                {/* Discipline Selection - Only for Staff and Physician */}
                {(role === "staff" || role === "physician") && (
                  <div className="space-y-2">
                    <Label htmlFor="discipline" className="text-sm font-medium">
                      Discipline <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="discipline"
                      value={discipline}
                      onChange={(e) => setDiscipline(e.target.value)}
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      <option value="">Select your discipline...</option>
                      <option value="rn">Registered Nurse (RN)</option>
                      <option value="lpn">Licensed Practical Nurse (LPN)</option>
                      <option value="msw">Medical Social Worker (MSW)</option>
                      <option value="chaplain">Chaplain/Spiritual Care</option>
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      data-testid="password-input"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      data-testid="confirm-password-input"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                  data-testid="signup-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-emerald-600 hover:underline font-medium">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Partnership CTA — for consultants, resellers, or enterprise inquiries */}
          <Card
            className="border-0 shadow-md bg-gradient-to-br from-slate-900 to-slate-800 text-white"
            data-testid="partnership-cta-card"
          >
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h3 className="font-semibold text-sm">Discuss a partnership?</h3>
                    <p className="text-xs text-slate-300 leading-snug mt-0.5">
                      Consultants, resellers, EMR integrators, and enterprise
                      hospice networks — reach out directly.
                    </p>
                  </div>
                  <div className="space-y-1.5 pt-1">
                    <a
                      href="mailto:nsarpong@goodwinliving.org?subject=HospiceGuard%20Partnership%20Inquiry"
                      className="flex items-center gap-2 text-xs text-emerald-300 hover:text-emerald-200 transition"
                      data-testid="partnership-email-link"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      nsarpong@goodwinliving.org
                    </a>
                    <a
                      href="tel:+15713296603"
                      className="flex items-center gap-2 text-xs text-emerald-300 hover:text-emerald-200 transition"
                      data-testid="partnership-phone-link"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      (571) 329-6603
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
    <PublicPageCopy page="register" withSeo />
    </>
  );
};

export default RegisterPage;
